import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonNote,
    IonPage,
    IonSpinner,
    IonTitle,
    IonToast,
    IonToolbar
} from "@ionic/react";

import { saveOutline, settingsOutline } from "ionicons/icons";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Cookies from "js-cookie";

import "./LeagueSettings.css";

const apiBaseUrl =
    "https://fantasy.ftblscore.com/create_league";

interface LeagueSettingsState {
    league: {
        id: string;
        name: string;
    };
    settings: Record<string, string>;
    playerSettings: Record<string, number>;
}

interface ScoringField {
    key: string;
    label: string;
    description: string;
}

interface ScoringGroup {
    title: string;
    description: string;
    fields: ScoringField[];
}

const scoringGroups: ScoringGroup[] = [
    {
        title: "Pase",
        description: "Puntos para quarterbacks y jugadas aéreas.",
        fields: [
            {
                key: "passYds",
                label: "Yardas por punto",
                description: "Ejemplo: 25 yardas = 1 punto."
            },
            {
                key: "passPoint",
                label: "Puntos por bloque de yardas",
                description: "Puntos otorgados por cada bloque."
            },
            {
                key: "passTds",
                label: "Touchdown por pase",
                description: "Puntos por TD lanzado."
            },
            {
                key: "passInt",
                label: "Intercepción lanzada",
                description: "Normalmente es un valor negativo."
            },
            {
                key: "pickTD",
                label: "Touchdown de intercepción",
                description: "Pick-six del quarterback."
            }
        ]
    },
    {
        title: "Acarreos y recepciones",
        description: "Reglas para RB, WR y jugadores FLEX.",
        fields: [
            {
                key: "rushYds",
                label: "Yardas terrestres por punto",
                description: "Ejemplo: 10 yardas = 1 punto."
            },
            {
                key: "rushPoint",
                label: "Puntos por bloque terrestre",
                description: "Puntos otorgados por cada bloque."
            },
            {
                key: "rushTds",
                label: "Touchdown terrestre",
                description: "Puntos por TD por acarreo."
            },
            {
                key: "rec",
                label: "Recepción",
                description: "Puntos PPR por recepción."
            },
            {
                key: "recYds",
                label: "Yardas recibidas por punto",
                description: "Ejemplo: 10 yardas = 1 punto."
            },
            {
                key: "recPoint",
                label: "Puntos por bloque recibido",
                description: "Puntos otorgados por cada bloque."
            },
            {
                key: "recTds",
                label: "Touchdown por recepción",
                description: "Puntos por TD recibido."
            },
            {
                key: "retTds",
                label: "Touchdown de retorno",
                description: "TD en devolución de patada o despeje."
            },
            {
                key: "conv",
                label: "Conversión de 2 puntos",
                description: "Puntos por conversión exitosa."
            }
        ]
    },
    {
        title: "Defensiva",
        description: "Eventos generados por la defensiva.",
        fields: [
            {
                key: "sack",
                label: "Captura",
                description: "Puntos por sack."
            },
            {
                key: "int",
                label: "Intercepción",
                description: "Puntos por intercepción defensiva."
            },
            {
                key: "fumbleF",
                label: "Fumble forzado",
                description: "Puntos por balón suelto provocado."
            },
            {
                key: "fumbleRec",
                label: "Fumble recuperado",
                description: "Puntos por balón suelto recuperado."
            },
            {
                key: "safety",
                label: "Safety",
                description: "Puntos por safety."
            },
            {
                key: "defTds",
                label: "Touchdown defensivo",
                description: "Puntos por TD de defensiva."
            },
            {
                key: "dretTds",
                label: "Touchdown de retorno defensivo",
                description: "Puntos por TD de retorno."
            },
            {
                key: "blockKick",
                label: "Patada bloqueada",
                description: "Puntos por bloqueo de patada."
            }
        ]
    },
    {
        title: "Pérdidas de balón",
        description: "Penalizaciones ofensivas.",
        fields: [
            {
                key: "fumble",
                label: "Fumble",
                description: "Puntos por balón suelto."
            },
            {
                key: "fumbleLost",
                label: "Fumble perdido",
                description: "Puntos por balón suelto perdido."
            }
        ]
    },
    {
        title: "Kicker",
        description: "Puntuación para goles de campo y puntos extra.",
        fields: [
            {
                key: "extraPoint",
                label: "Punto extra anotado",
                description: "PAT convertido."
            },
            {
                key: "patFailed",
                label: "Punto extra fallado",
                description: "PAT no convertido."
            },
            {
                key: "fg20",
                label: "Gol de campo de 20-29 yd",
                description: "Puntos por FG corto."
            },
            {
                key: "fg30",
                label: "Gol de campo de 30-39 yd",
                description: "Puntos por FG."
            },
            {
                key: "fg40",
                label: "Gol de campo de 40-49 yd",
                description: "Puntos por FG largo."
            },
            {
                key: "fg50",
                label: "Gol de campo de 50+ yd",
                description: "Puntos por FG de 50 o más."
            },
            {
                key: "fgFailed",
                label: "Gol de campo fallado",
                description: "Penalización por FG no convertido."
            }
        ]
    },
    {
        title: "Puntos y yardas permitidas",
        description: "Bonos o penalizaciones para DEF.",
        fields: [
            {
                key: "pts0",
                label: "0 puntos permitidos",
                description: "Defensa con blanqueada."
            },
            {
                key: "pts6",
                label: "1 a 6 puntos permitidos",
                description: "Rango de puntos permitidos."
            },
            {
                key: "pts13",
                label: "7 a 13 puntos permitidos",
                description: "Rango de puntos permitidos."
            },
            {
                key: "pts20",
                label: "14 a 20 puntos permitidos",
                description: "Rango de puntos permitidos."
            },
            {
                key: "pts27",
                label: "21 a 27 puntos permitidos",
                description: "Rango de puntos permitidos."
            },
            {
                key: "pts34",
                label: "28 a 34 puntos permitidos",
                description: "Rango de puntos permitidos."
            },
            {
                key: "pts35",
                label: "35+ puntos permitidos",
                description: "Rango de puntos permitidos."
            },
            {
                key: "yds100",
                label: "Menos de 100 yardas permitidas",
                description: "Bono adicional para DEF."
            }
        ]
    }
];

const rosterFields = [
    { key: "qb", label: "Quarterbacks", position: "QB" },
    { key: "rb", label: "Running backs", position: "RB" },
    { key: "wr", label: "Wide receivers", position: "WR" },
    { key: "wb", label: "Flex", position: "FLEX" },
    { key: "k", label: "Kickers", position: "K" },
    { key: "def", label: "Defensivas", position: "DEF" },
    { key: "bn", label: "Banca", position: "BN" }
];

function getAuthHeaders(): HeadersInit {
    return {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Access-Username":
            Cookies.get("access_username") || "",
        "X-Access-Token":
            Cookies.get("access_token") || ""
    };
}



const LeagueSettings: React.FC = () => {
    const { id } = useParams<{ id: string }>();

    const [data, setData] =
        useState<LeagueSettingsState | null>(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [loadError, setLoadError] = useState("");


    const loadSettings = useCallback(async function () {
    try {
        setLoadError("");
setMessage("");

        setLoading(true);
        setMessage("");

        const endpoint =
            `${apiBaseUrl}` +
            `/control_FantasyLeagueSettingsState.php` +
            `?id=${encodeURIComponent(id)}` +
            `&t=${Date.now()}`;

        const response = await fetch(endpoint, {
            method: "GET",
            credentials: "include",
            headers: {
                Accept: "application/json",
                "X-Access-Username":
                    Cookies.get("access_username") || "",
                "X-Access-Token":
                    Cookies.get("access_token") || ""
            },
            cache: "no-store"
        });

        console.log(
    "LeagueSettings URL:",
    response.url
);

console.log(
    "LeagueSettings HTTP:",
    response.status,
    response.statusText
);

        const rawResponse = await response.text();

        let result: any;

        try {
            result = JSON.parse(rawResponse);
        } catch {
            throw new Error(
                `El servidor no respondió JSON. ` +
                `HTTP ${response.status}. ` +
                `Verifica que exista control_FantasyLeagueSettingsState.php.`
            );
        }

        if (!response.ok || !result.success) {
            throw new Error(
                result.message ||
                    `No fue posible cargar la configuración. ` +
                    `HTTP ${response.status}.`
            );
        }

        setData({
            league: result.league,
            settings: result.settings || {},
            playerSettings: result.playerSettings || {}
        });
    } catch (error) {
    const errorMessage =
        error instanceof Error
            ? error.message
            : "No fue posible cargar la configuración.";

    console.error(
        "Error al cargar LeagueSettings:",
        error
    );

    setLoadError(errorMessage);
    setMessage(errorMessage);
    setData(null);
} finally {
    setLoading(false);
}

}, [id]);


    useEffect(function () {
        loadSettings();
    }, [loadSettings]);

    function updateSetting(key: string, value: string) {
        setData(function (currentData) {
            if (!currentData) {
                return currentData;
            }

            return {
                ...currentData,
                settings: {
                    ...currentData.settings,
                    [key]: value
                }
            };
        });
    }

    function updateLeagueName(value: string) {
    setData(function (currentData) {
        if (!currentData) {
            return currentData;
        }

        return {
            ...currentData,
            league: {
                ...currentData.league,
                name: value
            }
        };
    });
}

    function updatePlayerSetting(key: string, value: string) {
        const amount = Math.max(0, Number.parseInt(value, 10) || 0);

        setData(function (currentData) {
            if (!currentData) {
                return currentData;
            }

            return {
                ...currentData,
                playerSettings: {
                    ...currentData.playerSettings,
                    [key]: amount
                }
            };
        });
    }

    async function saveSettings() {
        if (!data || saving) {
            return;
        }

        try {
            setSaving(true);

            const response = await fetch(
    `${apiBaseUrl}/control_UpdateFantasyLeagueSettings.php`,
    {
        method: "POST",
        credentials: "include",
        headers: getAuthHeaders(),
        body: JSON.stringify({
            leagueId: id,
            leagueName: data.league.name,
            settings: data.settings,
            playerSettings: data.playerSettings
        })
    }
);


            const rawResponse = await response.text();

let result: {
    success?: boolean;
    message?: string;
    league?: {
        id: string;
        name: string;
    };
    settings?: Record<string, string>;
    playerSettings?: Record<string, number>;
};

try {
    result = JSON.parse(rawResponse);
} catch {
    console.error(
        "control_UpdateFantasyLeagueSettings.php no devolvió JSON:",
        {
            status: response.status,
            statusText: response.statusText,
            response: rawResponse
        }
    );

    throw new Error(
        rawResponse.trim()
            ? `El servidor devolvió una respuesta no válida. HTTP ${response.status}.`
            : `El servidor devolvió una respuesta vacía. HTTP ${response.status}. Revisa el log de PHP.`
    );
}

if (!response.ok || !result.success) {
    throw new Error(
        result.message ||
            `No fue posible guardar los cambios. HTTP ${response.status}.`
    );
}


            setData(function (currentData) {
                if (!currentData) {
                    return currentData;
                }

                return {
                    ...currentData,

                    league: result.league ??
                        currentData.league,

                    settings:
                        result.settings ??
                        currentData.settings,

                    playerSettings:
                        result.playerSettings ??
                        currentData.playerSettings
                };

            });

            setMessage(
                result.message ||
                    "Configuración actualizada correctamente."
            );
        } catch (error) {
            setMessage(
                error instanceof Error
                    ? error.message
                    : "No fue posible guardar los cambios."
            );
        } finally {
            setSaving(false);
        }
    }

function getIonInputStringValue(
    input: HTMLIonInputElement
): string {
    return input.value === null ||
        input.value === undefined
        ? ""
        : String(input.value);
}
    
    return (
        <IonPage>
            <IonHeader>
                <IonToolbar className="league-settings-toolbar">
                    <IonButtons slot="start">
                        <IonBackButton
                            defaultHref={`/fantasyLeague/${id}`}
                            text=""
                        />
                    </IonButtons>

                    <IonTitle>Configuración</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            disabled={!data || saving}
                            onClick={saveSettings}
                        >
                            <IonIcon slot="icon-only" icon={saveOutline} />

                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent className="league-settings-content">
                {loading && (
                    <div className="league-settings-loading">
                        <IonSpinner name="crescent" />
                        <span>Cargando configuración...</span>
                    </div>
                )}

                {!loading && !data && (
    <div className="league-settings-loading">
        <strong>
            No fue posible abrir la configuración.
        </strong>

        {loadError && (
            <p className="league-settings-error">
                {loadError}
            </p>
        )}

        <IonButton
            fill="outline"
            onClick={loadSettings}
        >
            Reintentar
        </IonButton>
    </div>
)}

                {data && (
                    <main className="league-settings-main">
                        <section className="league-settings-hero">
                            <IonIcon icon={settingsOutline} />


                            <div>
                                <span>COMISIONADO</span>
                                <h1>{data.league.name}</h1>
                                <p>
                                    Administra la puntuación y la
                                    composición de los rosters.
                                </p>
                            </div>
                        </section>

<section className="league-settings-section">
    <div className="league-settings-heading">
        <span>LIGA</span>
        <h2>Nombre de la liga</h2>
        <p>
            Este nombre será visible para todos los participantes.
        </p>
    </div>

    <IonItem className="league-settings-item">
        <IonLabel position="stacked">
            <strong>Nombre</strong>
        </IonLabel>

        <IonInput
            className="league-settings-text"
            value={data.league.name}
            maxlength={20}
            placeholder="Nombre de la liga"
            onIonInput={function (event) {
                const input =
                    event.currentTarget as HTMLIonInputElement;

                updateLeagueName(
                    getIonInputStringValue(input)
                );
            }}
        />
    </IonItem>
</section>

                        <section className="league-settings-section">
                            <div className="league-settings-heading">
                                <span>ROSTER</span>
                                <h2>Jugadores por equipo</h2>
                            </div>

                            <div className="league-settings-roster-grid">
                                {rosterFields.map(function (field) {
                                    return (
                                        <IonItem
                                            key={field.key}
                                            className="league-settings-item"
                                        >
                                            <IonLabel>
                                                <strong>
                                                    {field.label}
                                                </strong>
                                                <p>
                                                    {field.position}
                                                </p>
                                            </IonLabel>

                                            <IonInput
                                                className="league-settings-number"
                                                type="number"
                                                min="0"
                                                inputMode="numeric"
                                                value={
                                                    data.playerSettings[
                                                        field.key
                                                    ] ?? 0
                                                }
                                                onIonChange={function (event) {
                                                    updatePlayerSetting(
                                                        field.key,
                                                        String(event.detail.value || 0)
                                                    );
                                                }}

                                            />
                                        </IonItem>
                                    );
                                })}
                            </div>
                        </section>

                        {scoringGroups.map(function (group) {
                            return (
                                <section
                                    className="league-settings-section"
                                    key={group.title}
                                >
                                    <div className="league-settings-heading">
                                        <span>PUNTUACIÓN</span>
                                        <h2>{group.title}</h2>
                                        <p>{group.description}</p>
                                    </div>

                                    <div className="league-settings-fields">
                                        {group.fields.map(function (
                                            field
                                        ) {
                                            return (
                                                <IonItem
                                                    key={field.key}
                                                    className="league-settings-item"
                                                >
                                                    <IonLabel>
                                                        <strong>
                                                            {field.label}
                                                        </strong>
                                                        <p>
                                                            {
                                                                field.description
                                                            }
                                                        </p>
                                                    </IonLabel>

                                                    <IonInput
                                                        className="league-settings-number"
                                                        type="number"
                                                        inputMode="decimal"
                                                        value={
                                                            data.settings[
                                                                field.key
                                                            ] ?? ""
                                                        }
                                                        placeholder="0"
                                                        onIonChange={function (event) {
    updateSetting(
        field.key,
        String(event.detail.value || "")
    );
}}

                                                    />
                                                </IonItem>
                                            );
                                        })}
                                    </div>
                                </section>
                            );
                        })}

                        <IonNote className="league-settings-note">
                            Los valores vacíos se conservan como reglas
                            desactivadas. Los cambios se aplicarán a los
                            cálculos fantasy posteriores.
                        </IonNote>

                        <IonButton
                            expand="block"
                            className="league-settings-save"
                            disabled={saving}
                            onClick={saveSettings}
                        >
                            {saving
                                ? "Guardando cambios..."
                                : "Guardar configuración"}
                        </IonButton>
                    </main>
                )}
            </IonContent>

            <IonToast
                isOpen={Boolean(message)}
                message={message}
                duration={3500}
                position="bottom"
                onDidDismiss={function () {
                    setMessage("");
                }}
            />
        </IonPage>
    );
};

export default LeagueSettings;
